export const PLAYER_ROLE_HUMAN = "human";
export const PLAYER_ROLE_CPU = "cpu";
export const DEFAULT_CPU_DIFFICULTY = "normal";

export function createPlayer(name, options = {}) {
  const role = options.role === PLAYER_ROLE_CPU ? PLAYER_ROLE_CPU : PLAYER_ROLE_HUMAN;
  const cpuDifficulty = role === PLAYER_ROLE_CPU ? options.cpuDifficulty ?? DEFAULT_CPU_DIFFICULTY : null;

  return {
    name,
    role,
    cpuDifficulty,
    cpuMemory: role === PLAYER_ROLE_CPU ? createEmptyCpuMemory() : null,
    hand: [],
    faceUp: [],
    faceDown: [],
    tableSlots: [],
    status: "Playing"
  };
}

export function isCpuPlayer(player) {
  return Boolean(player && player.role === PLAYER_ROLE_CPU);
}

export function isHumanPlayer(player) {
  return Boolean(player && player.role !== PLAYER_ROLE_CPU);
}

export function getPlayableCards(player) {
  if (player.hand.length > 0) {
    return player.hand;
  }
  if (player.faceUp.length > 0) {
    return player.faceUp;
  }
  if (player.faceDown.length > 0) {
    return player.faceDown;
  }
  return [];
}

export function getPlayableZone(player) {
  if (player.hand.length > 0) {
    return "hand";
  }
  if (player.faceUp.length > 0) {
    return "faceUp";
  }
  if (player.faceDown.length > 0) {
    return "faceDown";
  }
  return null;
}

export function onlyHasFaceDownCards(player) {
  return player.hand.length === 0 && player.faceUp.length === 0 && player.faceDown.length > 0;
}

export function hasAnyCards(player) {
  return player.hand.length > 0 || player.faceUp.length > 0 || player.faceDown.length > 0;
}

export function findCardInPlayerCards(player, cardId) {
  const handIndex = player.hand.findIndex((card) => card.id === cardId);
  if (handIndex >= 0) {
    return { zone: "hand", index: handIndex, card: player.hand[handIndex] };
  }

  const faceUpIndex = player.faceUp.findIndex((card) => card.id === cardId);
  if (faceUpIndex >= 0) {
    return { zone: "faceUp", index: faceUpIndex, card: player.faceUp[faceUpIndex] };
  }

  const faceDownIndex = player.faceDown.findIndex((card) => card.id === cardId);
  if (faceDownIndex >= 0) {
    return { zone: "faceDown", index: faceDownIndex, card: player.faceDown[faceDownIndex] };
  }

  return null;
}

export function resetTableSlots(player) {
  if (!player) {
    return [];
  }

  const faceUpCards = getPlayerZoneCards(player, "faceUp");
  const faceDownCards = getPlayerZoneCards(player, "faceDown");
  player.tableSlots = createTableSlotMetadata(faceUpCards, faceDownCards);
  return player.tableSlots;
}

export function replaceTableSlotFaceUpCard(player, previousCardId, nextCardId, fallbackIndex = -1) {
  if (!player) {
    return false;
  }

  ensureTableSlotsForMutation(player);
  const slot = findTableSlotByCardId(player.tableSlots, "faceUp", previousCardId)
    ?? getTableSlotByIndex(player.tableSlots, fallbackIndex);
  if (!slot) {
    return false;
  }

  slot.faceUpCardId = nextCardId ?? null;
  return true;
}

export function swapTableSlotFaceUpCards(player, firstCardId, secondCardId, firstFallbackIndex = -1, secondFallbackIndex = -1) {
  if (!player || firstCardId === secondCardId) {
    return false;
  }

  ensureTableSlotsForMutation(player);
  const firstSlot = findTableSlotByCardId(player.tableSlots, "faceUp", firstCardId)
    ?? getTableSlotByIndex(player.tableSlots, firstFallbackIndex);
  const secondSlot = findTableSlotByCardId(player.tableSlots, "faceUp", secondCardId)
    ?? getTableSlotByIndex(player.tableSlots, secondFallbackIndex);
  if (!firstSlot || !secondSlot || firstSlot === secondSlot) {
    return false;
  }

  const firstSlotCardId = firstSlot.faceUpCardId;
  firstSlot.faceUpCardId = secondSlot.faceUpCardId ?? null;
  secondSlot.faceUpCardId = firstSlotCardId ?? null;
  return true;
}

export function clearTableSlotCard(player, zone, cardId, fallbackIndex = -1) {
  const slotKey = getTableSlotCardKey(zone);
  if (!player || !slotKey) {
    return false;
  }

  ensureTableSlotsForMutation(player);
  const slot = findTableSlotByCardId(player.tableSlots, zone, cardId)
    ?? getTableSlotByIndex(player.tableSlots, fallbackIndex);
  if (!slot) {
    return false;
  }

  slot[slotKey] = null;
  return true;
}

export function getTableSlots(player) {
  const faceUpCards = getPlayerZoneCards(player, "faceUp");
  const faceDownCards = getPlayerZoneCards(player, "faceDown");
  const faceUpById = createCardIdMap(faceUpCards);
  const faceDownById = createCardIdMap(faceDownCards);
  const sourceSlots = hasCompleteTableSlotCoverage(player?.tableSlots, faceUpCards, faceDownCards)
    ? player.tableSlots
    : createTableSlotMetadata(faceUpCards, faceDownCards);

  return sourceSlots.map((slot, index) => {
    const faceUpCardId = faceUpById.has(slot.faceUpCardId) ? slot.faceUpCardId : null;
    const faceDownCardId = faceDownById.has(slot.faceDownCardId) ? slot.faceDownCardId : null;

    return {
      index,
      faceUpCardId,
      faceDownCardId,
      faceUp: faceUpCardId ? faceUpById.get(faceUpCardId) : null,
      faceDown: faceDownCardId ? faceDownById.get(faceDownCardId) : null
    };
  });
}

function ensureTableSlotsForMutation(player) {
  const faceUpCards = getPlayerZoneCards(player, "faceUp");
  const faceDownCards = getPlayerZoneCards(player, "faceDown");
  if (!hasCompleteTableSlotCoverage(player.tableSlots, faceUpCards, faceDownCards)) {
    resetTableSlots(player);
  }
}

function createTableSlotMetadata(faceUpCards, faceDownCards) {
  const maxSlots = Math.max(faceUpCards.length, faceDownCards.length);
  return Array.from({ length: maxSlots }, (_, index) => ({
    faceUpCardId: faceUpCards[index]?.id ?? null,
    faceDownCardId: faceDownCards[index]?.id ?? null
  }));
}

function hasCompleteTableSlotCoverage(slots, faceUpCards, faceDownCards) {
  if (!Array.isArray(slots)) {
    return false;
  }

  const faceUpIds = new Set(faceUpCards.map((card) => card.id));
  const faceDownIds = new Set(faceDownCards.map((card) => card.id));
  const coveredFaceUpIds = new Set();
  const coveredFaceDownIds = new Set();

  for (const slot of slots) {
    if (faceUpIds.has(slot?.faceUpCardId)) {
      coveredFaceUpIds.add(slot.faceUpCardId);
    }
    if (faceDownIds.has(slot?.faceDownCardId)) {
      coveredFaceDownIds.add(slot.faceDownCardId);
    }
  }

  return coveredFaceUpIds.size === faceUpIds.size && coveredFaceDownIds.size === faceDownIds.size;
}

function getPlayerZoneCards(player, zone) {
  return Array.isArray(player?.[zone]) ? player[zone] : [];
}

function createCardIdMap(cards) {
  return new Map(cards.filter((card) => card?.id != null).map((card) => [card.id, card]));
}

function findTableSlotByCardId(slots, zone, cardId) {
  const slotKey = getTableSlotCardKey(zone);
  if (!slotKey || cardId == null || !Array.isArray(slots)) {
    return null;
  }

  return slots.find((slot) => slot?.[slotKey] === cardId) ?? null;
}

function getTableSlotByIndex(slots, index) {
  if (!Array.isArray(slots) || !Number.isInteger(index) || index < 0) {
    return null;
  }

  return slots[index] ?? null;
}

function getTableSlotCardKey(zone) {
  if (zone === "faceUp") {
    return "faceUpCardId";
  }
  if (zone === "faceDown") {
    return "faceDownCardId";
  }
  return null;
}

function createEmptyCpuMemory() {
  return {
    cards: {
      seenCards: [],
      remainingCards: []
    },
    opponent: {
      zones: {
        hand: {
          knownCards: []
        }
      }
    }
  };
}
